import React from 'react';
import classNames from 'classnames';
import { useRecoilValue } from 'recoil';
import { Button, SvgIcon } from '@ui-elements';
import { ActiveSpecialOffer } from '../../../special-offer/store';
import { TestDriveHref } from '../../store';
import { offerModelName } from '../../model-name';
import styles from './index.module.scss';

/**
 * The panel beside the enquiry form.
 *
 * Static by decision: the copy and the button are the same on every offer, and only the model name changes.
 * That name comes from the offer's category (see model-name.ts); an offer that is not about one model - a
 * servicing or accessory offer - gets the generic wording instead.
 *
 * Two consequences worth knowing:
 *  - The `bluebox-ctas` and `sub-bluebox-ctas` an editor may have authored for the existing page are NOT
 *    rendered here, because this panel is not editor-driven. Offers moved to this design lose those links.
 *  - The button goes to the generic booking page without a model, since the offer carries no model code,
 *    only the category text. An offer that needs a particular booking page - a model-specific one among
 *    them - overrides the destination on its "Book a test drive" CTA, which is what TestDriveHref reads.
 */

interface TestDrivePanelProps {
  className?: string;
}

export const TestDrivePanel: React.FC<TestDrivePanelProps> = ({
  className,
}) => {
  const offer = useRecoilValue(ActiveSpecialOffer);
  const bookHref = useRecoilValue(TestDriveHref);
  // Title as well as category: most live offers sit in the general "Special Offer" category, and the model
  // is named in the title ("Subaru Outback AWD Premium").
  const model = offerModelName(offer?.category, offer?.title);

  return (
    <aside
      className={classNames(styles.panel, className)}
      data-testid="offer-test-drive-panel"
    >
      <div className={styles.inner}>
        <SvgIcon className={styles.icon} type="drive" size={4} color="#fff" />

        <h2 className={styles.heading} data-testid="offer-test-drive-heading">
          {model ? `Test Drive the Subaru ${model}` : 'Book a test drive'}
        </h2>

        <p className={styles.blurb}>
          Don&apos;t miss out on this limited time offer!
          <br />
          Hit the button below to book a test drive at your preferred Subaru
          Retailer.
        </p>

        <Button
          buttonSize="large"
          className={styles.action}
          href={bookHref}
          type="borderedSecondary"
          data-testid="offer-test-drive-button"
        >
          Book a test drive
        </Button>
      </div>
    </aside>
  );
};
